import "../../styles/alert-details.scss";
import { useEffect, useState, useMemo, useRef, useCallback, memo } from "react";
import { useSelector } from "react-redux";
import * as DOMPurify from "dompurify";
import actions from "../../actions/actions";
import { AlertStatus, AlertType, isMobile, MAX_DISPLAY } from "../../utilities/constants";
import { formatDateLong, useUpdateState } from "../../utilities/utilities";
import CircularProgress from "../../actions/CircularProgress";
import { ConfirmDeleteModal } from "../../components/confirm-delete-modal";
import AudioPlayer from "../../components/audio-player";
import LoadingSpinner from "../../components/loading-spinner";
import { showToast } from "../../components/toast";
import PSAnalytic from "../ParishSoft/PSAnalytic";

function getPickerType(recipientType) {
  switch (recipientType) {
    case 1:
      return "member";
    case 2:
      return "family";
    case 3:
      return "memberWorkgroup";
    case 4:
      return "familyWorkgroup";
    default:
      return undefined;
  }
}

function buildGroupsPayload(alert) {
  return Array.isArray(alert.groups)
    ? alert.groups.map((g) => {
        if (typeof g !== "object") {
          return { id: g, name: alert.allGroups ? "All Users" : "" };
        }
        const pickerType = getPickerType(g.recipient_type);
        const workgroupSelectionId =
          (pickerType === "memberWorkgroup" || pickerType === "familyWorkgroup") &&
          g.group_code
            ? Number(g.group_code)
            : null;
        const isFamily = pickerType === "family" && !!g.familyDUID;
        return {
          id:
            isFamily && g.familyDUID
              ? g.familyDUID
              : pickerType === "member" && g.memberDUID
                ? g.memberDUID
                : workgroupSelectionId || g.hgsGroupId || g.id,
          hgsGroupId: g.hgsGroupId || g.id,
          familyDUID: g.familyDUID || null,
          memberDUID: g.memberDUID || null,
          group_code: g.group_code || null,
          name: g.name,
          _pickerType: pickerType,
          recipient_type: g.recipient_type,
        };
      })
    : [];
}

export default function AlertDetailsEmbed() {
  let alert = useSelector((store) => store.alert.current);
  const [smsInboundDetails, setSmsInboundDetails] = useState(null);
  const [alertId, setAlertId] = useState(0);
  const [activeTab, setActiveTab] = useState("message");
  const [state, updateState] = useUpdateState({});
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [showAllBody, setShowAllBody] = useState(false);
  const [showAllInbound, setShowAllInbound] = useState(false);
  const bodyRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [audioUrls, setAudioUrls] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const actionMenuRef = useRef(null);

  const groups = useMemo(() => alert?.groups ?? [], [alert]);
  const { regularGroups, tempGroups } = useMemo(() => {
    const regular = [];
    const temp = [];
    for (const g of groups) {
      if (g?.is_temp === 1) temp.push(g);
      else regular.push(g);
    }
    return { regularGroups: regular, tempGroups: temp };
  }, [groups]);

  function handleCancel() {
    window.history.length > 1
      ? window.history.back()
      : window.location.assign("/messages-embed");
  }

  function handleUseAsDraft() {
    if (alert) {
      const groups = buildGroupsPayload(alert);
      const mapped = {
        id: alert.id,
        name: alert.name,
        scheduledDate: alert.scheduledDate,
        groups,
        hgsGroups: groups
          .filter((g) => typeof g === "object" && g.hgsGroupId > 0)
          .map((g) => ({ id: g.hgsGroupId, name: g.name })),
        allGroups: alert.allGroups,
        isEmergency: alert.isEmergency,
        status: AlertStatus.Draft,
        attachments: alert.attachments || [],
        alertType: alert.alertType,
        message: alert.message,
        voiceFileUrl: alert.voiceFileUrl,
        voiceFileIdentifier: alert.voiceFileIdentifier,
        includeText: alert.alertType === AlertType.Text,
        smsMessage: alert.alertType === AlertType.Text ? alert.message : undefined,
        includeEmail: alert.alertType === AlertType.Email,
        emailMessage: alert.alertType === AlertType.Email ? alert.message : undefined,
        includeVoice: alert.alertType === AlertType.Voice,
        t2sMessage: alert.alertType === AlertType.Voice ? alert.message : undefined,
        t2sUrl: alert.alertType === AlertType.Voice ? alert.voiceFileUrl : undefined,
      };
      actions.embedAlerts.showEditAlert(mapped, "/messages-embed");
    }
  }

  const handleEdit = useCallback(() => {
    if (!alert) return;
    const mappedGroups = buildGroupsPayload(alert);
    const mapped = {
      id: alert.id,
      name: alert.name,
      scheduledDate: alert.scheduledDate,
      groups: mappedGroups,
      hgsGroups: mappedGroups
        .filter((g) => typeof g === "object" && g.hgsGroupId > 0)
        .map((g) => ({ id: g.hgsGroupId, name: g.name })),
      allGroups: alert.allGroups,
      isEmergency: alert.isEmergency,
      status: alert.status,
      attachments: alert.attachments || [],
      alertType: alert.alertType,
      message: alert.message,
      voiceFileUrl: alert.voiceFileUrl,
      voiceFileIdentifier: alert.voiceFileIdentifier,
      includeText: alert.alertType === AlertType.Text,
      smsMessage: alert.alertType === AlertType.Text ? alert.message : undefined,
      includeEmail: alert.alertType === AlertType.Email,
      emailMessage: alert.alertType === AlertType.Email ? alert.message : undefined,
      includeVoice: alert.alertType === AlertType.Voice,
      t2sMessage: alert.alertType === AlertType.Voice ? alert.message : undefined,
      t2sUrl: alert.alertType === AlertType.Voice ? alert.voiceFileUrl : undefined,
    };
    actions.embedAlerts.showEditAlert(mapped, "/messages-embed");
  }, [alert]);

  const handleDelete = useCallback(async () => {
    if (!alert?.id) return;
    updateState({ deletingAlert: true });
    const groupId = alert.groups?.[0]?.hgsGroupId || alert.groups?.[0]?.id || 0;
    const payload = [{ alertId: alert.id, groupId }];
    const result = await actions.parishSoftApis.bulkDelete(payload);
    updateState({ deletingAlert: false, deleteConfirmClose: false });
    if (result?.success) {
      showToast("removed", "Message deleted successfully.");
      handleCancel();
    } else {
      showToast("error", "Unable to delete the message. Please try again.");
    }
  }, [alert, updateState]);

  // Track mounted state to avoid setState on unmounted component
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const details = JSON.parse(localStorage.getItem("smsInboundDetails"));
    if (details) {
      setSmsInboundDetails(details);
    }
    let params = new URLSearchParams(window.location.search);
    let id = parseInt(params.get("id")) || 0;
    setAlertId(id);
    if (id !== 0) {
      actions.parishSoftApis.messagePreview(id);
    }
  }, []);

  useEffect(() => {
    if (alert?.alertType === AlertType.Voice) {
      if (alert?.voiceFileIdentifier && alert.voiceFileIdentifier !== "Pending") {
        setAudioUrls(alert?.voiceFileUrl);
      } else if (alert?.message) {
        (async () => {
          try {
            const response = await actions.parishSoftApis.textToSpeech(alert.message);
            if (response?.success && isMountedRef.current) {
              setAudioUrls(response?.fileUrl);
            }
          } catch (err) {
            console.error("Error during TTS conversion:", err);
          }
        })();
      }
    }
  }, [alert?.alertType, alert?.voiceFileIdentifier, alert?.voiceFileUrl, alert?.message]);

  const fetchTempGroupMembers = async (id) => {
    try {
      const details = await actions.groups.getAllGroupMembers(id);
      if (Array.isArray(details?.members)) {
        return details.members;
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
    }
    return [];
  };

  useEffect(() => {
    let isActive = true;
    const loadTempGroupMembers = async () => {
      if (!tempGroups.length) {
        if (isActive) { setGroupMembers([]); setShowAllMembers(false); }
        return;
      }
      const ids = Array.from(
        new Set(tempGroups.map((g) => g?.id ?? g?.groupId).filter(Boolean)),
      );
      if (!ids.length) {
        if (isActive) { setGroupMembers([]); setShowAllMembers(false); }
        return;
      }
      const memberLists = await Promise.all(ids.map(fetchTempGroupMembers));
      if (!isActive) return;
      const deduped = [];
      const seen = new Set();
      for (const list of memberLists) {
        if (!Array.isArray(list)) continue;
        for (const member of list) {
          const key =
            member?.userId ??
            `${member?.firstName || ""}-${member?.lastName || ""}-${member?.id ?? ""}`;
          if (!key || seen.has(key)) continue;
          seen.add(key);
          deduped.push(member);
        }
      }
      setGroupMembers(deduped);
      setShowAllMembers(false);
    };
    loadTempGroupMembers();
    return () => { isActive = false; };
  }, [tempGroups]);

  // Action menu keyboard / outside-click handling
  useEffect(() => {
    if (!state.showActionMenu) return;
    const handleDocumentClick = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        updateState({ showActionMenu: false });
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") { updateState({ showActionMenu: false }); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const items = actionMenuRef.current?.querySelectorAll(".ad-action-item");
        if (!items || items.length === 0) return;
        e.preventDefault();
        const arr = Array.from(items);
        const currentIndex = arr.findIndex((el) => el === document.activeElement);
        let nextIndex;
        if (currentIndex === -1) {
          nextIndex = e.key === "ArrowDown" ? 0 : arr.length - 1;
        } else {
          nextIndex =
            e.key === "ArrowDown"
              ? (currentIndex + 1) % arr.length
              : (currentIndex - 1 + arr.length) % arr.length;
        }
        arr[nextIndex].focus();
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [state.showActionMenu, updateState]);

  // Derived status label
  const alertStatus = alert?.status;
  const statusLabel = useMemo(() => {
    switch (alertStatus) {
      case AlertStatus.Draft: return "Draft";
      case AlertStatus.Scheduled: return "Scheduled";
      case AlertStatus.Sent: return "Sent";
      case AlertStatus.Error: return "Failed";
      default: return "Message";
    }
  }, [alertStatus]);

  const statusIconClass = useMemo(() => {
    switch (alertStatus) {
      case AlertStatus.Draft: return "fa fa-edit";
      case AlertStatus.Scheduled: return "fa fa-clock";
      case AlertStatus.Sent: return "fa fa-paper-plane";
      case AlertStatus.Error: return "fa fa-exclamation-circle";
      default: return "fa fa-info-circle";
    }
  }, [alertStatus]);

  const channelBadges = useMemo(() => {
    const badges = [];
    const hasAnyExplicit =
      alert.includeText || alert.includeEmail || alert.includeVoice || alert.includeVoiceIVR;
    if (hasAnyExplicit) {
      if (alert.includeText) badges.push({ key: "sms", label: "SMS", icon: "fa fa-comment-dots" });
      if (alert.includeEmail) badges.push({ key: "email", label: "Email", icon: "fa fa-envelope" });
      if (alert.includeVoice || alert.includeVoiceIVR)
        badges.push({ key: "voice", label: "Voice", icon: "fa fa-volume-up" });
    } else {
      switch (alert.alertType) {
        case AlertType.Text:
          badges.push({ key: "sms", label: "SMS", icon: "fa fa-comment-dots" }); break;
        case AlertType.Email:
          badges.push({ key: "email", label: "Email", icon: "fa fa-envelope" }); break;
        case AlertType.Voice:
          badges.push({ key: "voice", label: "Voice", icon: "fa fa-volume-up" }); break;
        default:
          badges.push({ key: "message", label: "Message", icon: "fa fa-comment" }); break;
      }
    }
    return badges;
  }, [alert.alertType, alert.includeText, alert.includeEmail, alert.includeVoice, alert.includeVoiceIVR]);

  const channelIconClass = useMemo(() => channelBadges[0]?.icon || "fa fa-comment", [channelBadges]);
  const channelLabel = useMemo(
    () => (channelBadges.length ? channelBadges.map((cb) => cb.label).join(", ") : "Message"),
    [channelBadges],
  );

  const regularGroupList = regularGroups;
  const groupChips = useMemo(
    () => (showAllGroups ? regularGroupList : regularGroupList.slice(0, MAX_DISPLAY)),
    [showAllGroups, regularGroupList],
  );
  const extraGroupCount = useMemo(
    () => (regularGroupList.length > MAX_DISPLAY ? regularGroupList.length - MAX_DISPLAY : 0),
    [regularGroupList],
  );

  const { visibleMembers, extraMembers, extraMembersTitle } = useMemo(() => {
    if (showAllMembers) return { visibleMembers: groupMembers, extraMembers: [], extraMembersTitle: "" };
    const visible = groupMembers.slice(0, MAX_DISPLAY);
    const extras = groupMembers.slice(MAX_DISPLAY);
    const title = extras
      .map((m) => [m?.firstName, m?.lastName].filter(Boolean).join(" "))
      .filter(Boolean)
      .join(", ");
    return { visibleMembers: visible, extraMembers: extras, extraMembersTitle: title };
  }, [groupMembers, showAllMembers]);

  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(alert.message || ""), [alert.message]);
  const plainTextFallback = useMemo(() => {
    if (!alert.message) {
      if (alert.alertType === AlertType.Voice && alert.voiceFileUrl)
        return "(Voice recording message)";
      return "(No message content)";
    }
    return alert.message.replace(/<[^>]+>/g, "").trim();
  }, [alert.message, alert.alertType, alert.voiceFileUrl]);

  useEffect(() => {
    if (bodyRef.current) {
      setIsTruncated(bodyRef.current.offsetHeight > 1000);
    }
  }, [sanitizedHtml, activeTab]);

  const inboundMessages = useMemo(() => {
    const list = alert.inboundList || [];
    return list.slice().sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
  }, [alert.inboundList]);

  const isMobileDevice = isMobile();
  const MOBILE_CHUNK = 10;
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_CHUNK);
  const mobileLoadMoreRef = useRef(null);

  useEffect(() => {
    if (!isMobileDevice) return;
    setMobileVisibleCount(() => Math.min(MOBILE_CHUNK, inboundMessages.length));
  }, [isMobileDevice, inboundMessages.length, activeTab]);

  useEffect(() => {
    if (!isMobileDevice) return;
    if (activeTab !== "message") return;
    if (mobileVisibleCount >= inboundMessages.length) return;
    const el = mobileLoadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setMobileVisibleCount((prev) => Math.min(prev + MOBILE_CHUNK, inboundMessages.length));
          }
        });
      },
      { root: null, rootMargin: "0px", threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobileDevice, activeTab, mobileVisibleCount, inboundMessages.length]);

  const inboundVisible = useMemo(() => {
    if (isMobileDevice) return inboundMessages.slice(0, mobileVisibleCount);
    return showAllInbound ? inboundMessages : inboundMessages.slice(0, 3);
  }, [isMobileDevice, showAllInbound, inboundMessages, mobileVisibleCount]);

  const messageCreatedRaw = alert.createdDate || null;
  const messageCreatedDisplay = formatInboundTimestamp(messageCreatedRaw);

  function hasAvatar(url) {
    return !!url && !!String(url).trim();
  }

  function formatInboundTimestamp(ts) {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      let hours = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12 || 12;
      const timePart = `${hours}:${minutes}${ampm}`;
      if (isToday) return `Today at ${timePart}`;
      return (
        d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + ` ${timePart}`
      );
    } catch (e) {
      return ts;
    }
  }

  const GroupChips = memo(function GroupChipsComponent({
    chips, showAll, extraCount, onShowAll, onShowLess, channelIcon, channelLabelText,
  }) {
    return (
      <div className="group-chips" aria-label="Recipient groups">
        {chips.map((g, index) => {
          const groupName = g?.name || "Group";
          return (
            <span
              className="group-chip"
              key={g?.id ?? g?.groupId ?? index}
              tabIndex={0}
              aria-label={`Group ${groupName} (${channelLabelText})`}
            >
              <i className={`${channelIcon} group-channel-icon`} aria-hidden="true"></i>
              <span className="group-chip-name">{groupName}</span>
              <i className="fa fa-users group-users-icon" aria-hidden="true"></i>
            </span>
          );
        })}
        {!showAll && extraCount > 0 && (
          <button type="button" className="group-chip more-chip"
            aria-label={`Show ${extraCount} more groups`} onClick={onShowAll}>
            +{extraCount} more
          </button>
        )}
        {showAll && extraCount > 0 && (
          <button type="button" className="group-chip less-chip"
            aria-label="Show fewer groups" onClick={onShowLess}>
            Show less
          </button>
        )}
      </div>
    );
  });

  const IndividualChips = memo(function IndividualChipsComponent({
    visible, extras, extrasTitle, showAll, totalCount, onShowAll, onShowLess,
  }) {
    return (
      <div className="group-chips" aria-label="Recipient individuals">
        {visible.map((member, index) => {
          const name = [member?.firstName, member?.lastName].filter(Boolean).join(" ");
          const key = member?.userId ?? `${name}-${index}`;
          return (
            <span className="group-chip individual-chip" key={key} tabIndex={0}
              aria-label={name ? `Individual ${name}` : "Recipient"}>
              <i className="fa fa-user group-channel-icon" aria-hidden="true"></i>
              <span className="group-chip-name">{name || "Recipient"}</span>
            </span>
          );
        })}
        {extras.length > 0 && !showAll && (
          <button type="button" className="group-chip more-chip"
            title={extrasTitle || undefined}
            aria-label={`Show ${extras.length} more individuals`} onClick={onShowAll}>
            +{extras.length} more
          </button>
        )}
        {showAll && totalCount > MAX_DISPLAY && (
          <button type="button" className="group-chip less-chip"
            aria-label="Show fewer individuals" onClick={onShowLess}>
            Show less
          </button>
        )}
      </div>
    );
  });

  if (!alert || (alertId !== 0 && alert.id !== alertId)) {
    return <LoadingSpinner />;
  }

  if (alertId === 0 && smsInboundDetails) {
    return (
      <div className="alert-details sms-inbound-details">
        <div className="page-title">SMS Inbound Details</div>
        <div className="container-card">
          <div className="field-container">
            <label>From</label>
            <div>{smsInboundDetails.createdUserName}</div>
          </div>
          <div className="field-container">
            <label>Received On</label>
            <div>{formatDateLong(smsInboundDetails.scheduled)}</div>
          </div>
          <div className="field-container">
            <label>Type</label>
            <div>{smsInboundDetails.categoryName}</div>
          </div>
          <div className="field-container">
            <label>Message</label>
            <div>{smsInboundDetails.messageBody}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="alert-details redesigned alert-details-embed">
      {/* Header Section */}
      <div className="ad-header" style={{ position: "relative" }}>
        <span
          id="back"
          className="nav-button"
          onClick={handleCancel}
          aria-label="Back to Messages"
        >
          <i className="far fa-chevron-left" aria-hidden="true"></i>
          <span className="nav-button-text">Messages</span>
        </span>
        <div className="ad-title-block" aria-label="Message header">
          <h2 className="ad-title" title={alert.name || "Message"} aria-label={alert.name || "Message"}>
            {alert.name || "Message"}
          </h2>
          <div className="ad-tags" aria-label="Message meta">
            <span
              className={`ad-tag status-${statusLabel.toLowerCase()}`}
              aria-label={`Status: ${statusLabel}`}
            >
              <i className={statusIconClass} aria-hidden="true"></i>
              <span className="ad-tag-text">{statusLabel}</span>
            </span>
            {channelBadges.map((cb) => (
              <span key={cb.key} className={`ad-tag channel-${cb.key}`} aria-label={`Channel: ${cb.label}`}>
                <i className={cb.icon} aria-hidden="true"></i>
                <span className="ad-tag-text">{cb.label}</span>
              </span>
            ))}
          </div>

          <div
            className={`ad-action-menu inline${state.showActionMenu ? " open" : ""}`}
            ref={actionMenuRef}
          >
            <button
              type="button"
              className="ad-action-trigger"
              aria-haspopup="true"
              aria-expanded={state.showActionMenu ? "true" : "false"}
              aria-label="Message actions"
              onClick={() => updateState({ showActionMenu: !state.showActionMenu })}
              onKeyDown={(e) => {
                if (e.key === "Escape") updateState({ showActionMenu: false });
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  const first = actionMenuRef.current?.querySelector(".ad-action-item");
                  first && first.focus();
                }
              }}
            >
              <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
            </button>
            {state.showActionMenu && (
              <ul className="ad-action-dropdown" role="menu" aria-label="Message action menu">
                {alert.status === AlertStatus.Sent && (
                  <li
                    role="menuitem" tabIndex={0} className="ad-action-item"
                    onClick={() => { updateState({ showActionMenu: false }); handleUseAsDraft(); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault(); handleUseAsDraft(); updateState({ showActionMenu: false });
                      }
                    }}
                    aria-label="Use message as draft"
                  >
                    <i className="fa fa-copy" aria-hidden="true"></i> Use as Draft
                  </li>
                )}
                {alert.status === AlertStatus.Sent && (
                  <li
                    role="menuitem" tabIndex={0} className="ad-action-item"
                    onClick={() => { updateState({ showActionMenu: false }); setActiveTab("analytics"); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault(); setActiveTab("analytics"); updateState({ showActionMenu: false });
                      }
                    }}
                    aria-label="View Details"
                  >
                    <i className="fa fa-chart-bar" aria-hidden="true"></i> View Details
                  </li>
                )}
                {(alert.status === AlertStatus.Draft || alert.status === AlertStatus.Scheduled) && (
                  <>
                    <li
                      role="menuitem" tabIndex={0} className="ad-action-item"
                      onClick={() => { updateState({ showActionMenu: false }); handleEdit(); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault(); handleEdit(); updateState({ showActionMenu: false });
                        }
                      }}
                      aria-label="Edit message"
                    >
                      <i className="fa fa-edit" aria-hidden="true"></i> Edit
                    </li>
                    <li
                      role="menuitem" tabIndex={0} className="ad-action-item"
                      onClick={() => { updateState({ showActionMenu: false, deleteConfirmClose: true }); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          updateState({ deleteConfirmClose: true, showActionMenu: false });
                        }
                      }}
                      aria-label="Delete message"
                    >
                      <i className="fa fa-trash" aria-hidden="true"></i> Delete
                    </li>
                  </>
                )}
                {alert.status === AlertStatus.Error && (
                  <li
                    role="menuitem" tabIndex={0} className="ad-action-item"
                    onClick={() => { updateState({ showActionMenu: false }); setActiveTab("analytics"); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault(); setActiveTab("analytics"); updateState({ showActionMenu: false });
                      }
                    }}
                    aria-label="View Details"
                  >
                    <i className="fa fa-chart-bar" aria-hidden="true"></i> View Details
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
        <div className="ad-tabs">
          <span
            className={`ad-tab${activeTab === "message" ? " active" : ""}`}
            onClick={() => setActiveTab("message")}
          >
            Message
          </span>
          {alert.status === AlertStatus.Sent && (
            <span
              className={`ad-tab${activeTab === "analytics" ? " active" : ""}`}
              onClick={() => setActiveTab("analytics")}
            >
              Analytics
            </span>
          )}
        </div>
      </div>

      {/* Message Card Section */}
      {activeTab === "message" && (
        <div className="ad-message-card" style={{ position: "relative" }}>
          <div className="ad-message-header">
            <div className="ad-header-row">
              <div className="ad-user-info">
                {hasAvatar(alert.profilePhotoURL) ? (
                  <img className="ad-avatar outlined" src={alert.profilePhotoURL} alt={alert?.senderName || "Sender"} />
                ) : (
                  <div className="ad-avatar ad-avatar-fallback outlined" aria-label="Default sender avatar">
                    <i className="fa fa-user" aria-hidden="true"></i>
                  </div>
                )}
                <span className="ad-user-name">{alert?.senderName || "Sender"}</span>
              </div>
              {messageCreatedRaw && (
                <div className="ad-message-time-right" title={messageCreatedRaw}
                  aria-label={`Message created ${messageCreatedDisplay}`}>
                  {messageCreatedDisplay}
                </div>
              )}
            </div>
            {regularGroupList.length > 0 && (
              <div className="ad-message-groups pad-left-avatar">
                <span className="ad-sent-label">Sent to</span>
                <GroupChips
                  chips={groupChips}
                  showAll={showAllGroups}
                  extraCount={extraGroupCount}
                  onShowAll={() => setShowAllGroups(true)}
                  onShowLess={() => setShowAllGroups(false)}
                  channelIcon={channelIconClass}
                  channelLabelText={channelLabel}
                />
              </div>
            )}
            {groupMembers.length > 0 && (
              <div className="ad-message-groups pad-left-avatar">
                <span className="ad-sent-label">Individuals</span>
                <IndividualChips
                  visible={visibleMembers}
                  extras={extraMembers}
                  extrasTitle={extraMembersTitle}
                  showAll={showAllMembers}
                  totalCount={groupMembers.length}
                  onShowAll={() => setShowAllMembers(true)}
                  onShowLess={() => setShowAllMembers(false)}
                />
              </div>
            )}
          </div>
          <div
            className="ad-message-body"
            aria-label="Message body"
          >
            <div className="ad-message-content pad-left-avatar">
              {alert.alertType !== AlertType.Voice && (
                <div className="ad-message-text">
                  {sanitizedHtml ? (
                    <div
                      ref={bodyRef}
                      className={`ad-body-html${isTruncated && !showAllBody ? " truncated" : ""}`}
                      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                    />
                  ) : (
                    <div className="ad-body-fallback">{plainTextFallback}</div>
                  )}
                  {isTruncated && (
                    <button type="button" className="ad-show-toggle"
                      aria-label={showAllBody ? "Show less content" : "Show full content"}
                      onClick={() => setShowAllBody(!showAllBody)}>
                      {showAllBody ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              )}
              {alert.alertType === AlertType.Voice && (
                <div>
                  <label className="voice-label">Sound File</label>
                  {alert.voiceFileUrl && (
                    <div className="voice-preview">
                      {audioUrls ? (
                        <AudioPlayer src={audioUrls} autoPlay={false} />
                      ) : (
                        <CircularProgress />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === "analytics" && alert?.status === AlertStatus.Sent && (
        <PSAnalytic alert={alert} />
      )}

      {/* Inbound replies (non-email messages only) */}
      {activeTab === "message" && alert.alertType !== AlertType.Email && (
        <>
          {!isMobileDevice && (
            <div className="ad-count-section-static">
              <div
                className={`ad-count-circle-static${showAllInbound ? " expanded" : ""}`}
                role="button"
                tabIndex={0}
                aria-label={showAllInbound ? "Collapse inbound replies" : `Show all ${inboundMessages.length} replies`}
                onClick={() => setShowAllInbound((s) => !s)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault(); setShowAllInbound((s) => !s);
                  }
                }}
              >
                {inboundMessages.length}
              </div>
              <div className="ad-count-divider-static"></div>
            </div>
          )}
          <div className="ad-comments-section">
            <div
              className={isMobileDevice ? "ad-comments-list" : `ad-comments-list${showAllInbound ? " scroll" : ""}`}
              aria-label="Replies list"
            >
              {inboundMessages.length === 0 && (
                <div className="ad-comment-empty" aria-label="No inbound replies">
                  No inbound replies yet.
                </div>
              )}
              {inboundVisible.map((m, idx) => (
                <div className="ad-comment" key={idx + m.createdDate}>
                  {hasAvatar(m.profilePhotoURL) ? (
                    <img className="ad-comment-avatar" src={m.profilePhotoURL} alt={m.userName} />
                  ) : (
                    <div className="ad-comment-avatar ad-comment-avatar-fallback"
                      aria-label={`Default avatar for ${m.userName}`}>
                      <i className="fa fa-user" aria-hidden="true"></i>
                    </div>
                  )}
                  <div className="ad-comment-content">
                    <span className="ad-comment-name">{m.userName}</span>
                    <span className="ad-comment-text">{m.message}</span>
                  </div>
                  <span className="ad-comment-time">{formatInboundTimestamp(m.createdDate)}</span>
                </div>
              ))}
              {isMobileDevice && mobileVisibleCount < inboundMessages.length && (
                <div className="ad-mobile-load-more" ref={mobileLoadMoreRef} aria-label="Load more replies">
                  Loading more...
                </div>
              )}
              {!isMobileDevice && !showAllInbound && inboundMessages.length > 3 && (
                <div className="ad-inline-more-hint" aria-label="Showing first 3 replies">
                  Showing 3 of {inboundMessages.length}. Click the count to view all.
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {state.deleteConfirmClose && (
        <ConfirmDeleteModal
          status="remove"
          onDelete={handleDelete}
          onCancel={() => updateState({ deleteConfirmClose: false })}
          deleting={state.deletingAlert}
        />
      )}
      <LoadingSpinner show={!alert.id} />
    </div>
  );
}
